import {type TraceCallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';

import {
  MAX_TIME_WINDOW_SECONDS,
  MAX_WINDOW_SIZE,
  SPAN_CUSTOM_ATTR_SOURCES,
} from './constants';
import {
  type AggregationFunction,
  type MetricType,
  type WeaveMetricThresholdConfig,
} from './types';
import {getValue} from './utils';

export const isValidMetricPath = (metricPath: string | undefined): boolean => {
  return metricPath !== undefined && metricPath.length > 0;
};

// A custom-attr metric path with no key after the prefix (e.g. `custom_attrs_float.`)
// resolves to nothing, so it is not a usable metric.
export const isIncompleteSpanCustomAttr = (metricPath: string): boolean =>
  SPAN_CUSTOM_ATTR_SOURCES.some(
    ({prefix}) =>
      metricPath.startsWith(prefix) && !metricPath.slice(prefix.length)
  );

export const isValidThreshold = (
  threshold: string | undefined,
  metricType?: MetricType,
  aggregationFunction?: AggregationFunction
): boolean => {
  if (threshold === undefined || threshold === '') {
    return false;
  }
  const thresholdNum = parseFloat(threshold);
  if (isNaN(thresholdNum) || !isFinite(thresholdNum)) {
    return false;
  }
  // Status/error-rate metrics aggregate as a percentage (a 0-100 rate) or a
  // count (any non-negative number).
  if (metricType === 'status' || metricType === 'span_status') {
    if (aggregationFunction === 'percentage') {
      return thresholdNum >= 0 && thresholdNum <= 100;
    }
    return thresholdNum >= 0;
  }
  return true;
};

export const isValidWindowSize = (windowSize: number | undefined): boolean => {
  return (
    windowSize !== undefined &&
    Number.isInteger(windowSize) &&
    windowSize > 0 &&
    windowSize <= MAX_WINDOW_SIZE
  );
};

export const isValidWindowDuration = (
  windowDuration: number | undefined
): boolean => {
  return (
    windowDuration !== undefined &&
    Number.isInteger(windowDuration) &&
    windowDuration > 0 &&
    windowDuration <= MAX_TIME_WINDOW_SECONDS
  );
};

const isValidSource = (config: WeaveMetricThresholdConfig): boolean => {
  const {sourceType, opStr, monitorRef} = config.metricFilter;
  if (sourceType === 'SPANS') {
    // Spans alerts evaluate the whole project; no op/monitor selection.
    return true;
  }
  if (sourceType === 'MONITOR') {
    // Monitor needs both a monitor ref and a scorer selection (stored in opStr)
    return (
      monitorRef !== undefined &&
      monitorRef.length > 0 &&
      opStr !== undefined &&
      opStr.length > 0
    );
  }
  return opStr !== undefined && opStr.length > 0;
};

export const isValidWeaveMetricThresholdConfig = (
  config: WeaveMetricThresholdConfig,
  callData?: TraceCallSchema
): boolean => {
  if (!config || !config.metricFilter) {
    return false;
  }

  const validSource = isValidSource(config);
  const validMetricPath =
    config.metricFilter.metricPath !== undefined &&
    config.metricFilter.metricPath.length > 0;
  const validThreshold = isValidThreshold(config.metricFilter.threshold);
  const hasValidWindow =
    isValidWindowSize(config.metricFilter.windowSize) ||
    isValidWindowDuration(config.metricFilter.windowDuration);

  if (callData && config.metricFilter.metricPath) {
    const value = getValue(callData, config.metricFilter.metricPath);
    if (typeof value === 'string') {
      return false;
    }
  }

  return validSource && validMetricPath && validThreshold && hasValidWindow;
};
